import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import type { InferResponseType } from 'hono/client'
import { api, rawFetch, unwrap } from '~/lib/api'

// Keys start with 'people' so the server's invalidate(['people']) refetches every squad view.
export const peopleKeys = {
  all: ['people'] as const,
  squad: (teamId: string, season: string) => ['people', 'squad', teamId, season] as const,
  person: (id: string, teamId: string, season: string) => ['people', 'person', id, teamId, season] as const,
  attendance: (id: string, teamId: string, season: string) => ['people', 'attendance', id, teamId, season] as const,
  assessments: (id: string) => ['people', 'assessments', id] as const,
  fields: (teamId: string) => ['people', 'fields', teamId] as const,
  officials: (teamId: string) => ['people', 'officials', teamId] as const,
  duplicates: ['people', 'duplicates'] as const,
  mergeCandidates: (id: string, q: string) => ['people', 'merge-candidates', id, q] as const,
}

// Response types named here (not inferred) so declaration emit doesn't reach into API internals.
export type Squad = InferResponseType<(typeof api.teams)[':teamId']['squad']['$get'], 200>
export type PersonHub = InferResponseType<(typeof api.people)[':id']['$get'], 200>

export const squadQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: peopleKeys.squad(teamId, season),
    queryFn: (): Promise<Squad> => unwrap(api.teams[':teamId'].squad.$get({ param: { teamId }, query: { season } })),
  })

export const personQuery = (id: string, teamId: string, season: string) =>
  queryOptions({
    queryKey: peopleKeys.person(id, teamId, season),
    queryFn: (): Promise<PersonHub> => unwrap(api.people[':id'].$get({ param: { id }, query: { teamId, season } })),
  })

export const personAttendanceQuery = (id: string, teamId: string, season: string) =>
  queryOptions({
    queryKey: peopleKeys.attendance(id, teamId, season),
    queryFn: () => unwrap(api.people[':id'].attendance.$get({ param: { id }, query: { teamId, season } })),
  })

export const assessmentsQuery = (id: string) =>
  queryOptions({
    queryKey: peopleKeys.assessments(id),
    queryFn: () => unwrap(api.people[':id'].assessments.$get({ param: { id } })),
  })

export const fieldsQuery = (teamId: string) =>
  queryOptions({
    queryKey: peopleKeys.fields(teamId),
    queryFn: () => unwrap(api.teams[':teamId'].squad.fields.$get({ param: { teamId } })),
    staleTime: Infinity,
  })

export const officialsQuery = (teamId: string) =>
  queryOptions({
    queryKey: peopleKeys.officials(teamId),
    queryFn: (): Promise<OfficialCandidate[]> => unwrap(api.teams[':teamId'].squad.officials.$get({ param: { teamId } })),
  })
export type OfficialCandidate = InferResponseType<(typeof api.teams)[':teamId']['squad']['officials']['$get'], 200>[number]

type OfficialBody = Parameters<(typeof api.teams)[':teamId']['squad']['officials']['$post']>[0]['json']
export function useTakeOverOfficial(teamId: string, season: string) {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: (json: OfficialBody) => unwrap(api.teams[':teamId'].squad.officials.$post({ param: { teamId }, query: { season }, json })),
    onSuccess: done,
  })
}

export type SquadEntry = Squad['players'][number]
export type Membership = PersonHub['memberships'][number]

// --- Duplicates and merging ------------------------------------------------------------------

export type DuplicatePair = InferResponseType<typeof api.people.duplicates.$get, 200>[number]
export type MergeSide = DuplicatePair['a']
export type MergePreview = InferResponseType<(typeof api.people)[':id']['merge']['preview']['$post'], 200>
export type MergeCandidate = InferResponseType<(typeof api.people)[':id']['merge-candidates']['$get'], 200>[number]

export const duplicatesQuery = () =>
  queryOptions({
    queryKey: peopleKeys.duplicates,
    queryFn: (): Promise<DuplicatePair[]> => unwrap(api.people.duplicates.$get()),
    // Every /kader visit would recompute it; a change to people invalidates it anyway (SSE).
    staleTime: 5 * 60_000,
  })

export const mergeCandidatesQuery = (id: string, q: string) =>
  queryOptions({
    queryKey: peopleKeys.mergeCandidates(id, q),
    queryFn: (): Promise<MergeCandidate[]> => unwrap(api.people[':id']['merge-candidates'].$get({ param: { id }, query: q ? { q } : {} })),
    placeholderData: (prev) => prev,
  })

/**
 * Dry run of one direction. Outside the 'people' prefix on purpose: a merge invalidates 'people',
 * and the preview of a person that no longer exists must not refetch.
 */
export const mergePreviewQuery = (keepId: string, mergeId: string) =>
  queryOptions({
    queryKey: ['merge-preview', keepId, mergeId] as const,
    queryFn: (): Promise<MergePreview> => unwrap(api.people[':id'].merge.preview.$post({ param: { id: keepId }, json: { mergeId } })),
    staleTime: 0,
    gcTime: 0,
  })

export function useMergePeople() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ keepId, mergeId }: { keepId: string; mergeId: string }) => unwrap(api.people[':id'].merge.$post({ param: { id: keepId }, json: { mergeId } })),
    // Everything about both people changed: matches, training, finance, medical.
    onSuccess: () => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'merge-preview' }),
  })
}

/** No invalidation here: the sheet keeps the row (with undo) until it closes, then refetches. */
export function useDismissDuplicate() {
  return useMutation({
    mutationFn: (json: { aId: string; bId: string; undo?: boolean }) => unwrap(api.people.duplicates.dismiss.$post({ json })),
  })
}

/** After any write: everything under 'people' and 'stats' (names, numbers, minutes). */
export function useInvalidatePeople() {
  const qc = useQueryClient()
  return () => Promise.all([qc.invalidateQueries({ queryKey: peopleKeys.all }), qc.invalidateQueries({ queryKey: ['stats'] })])
}

type CreateBody = Parameters<(typeof api.teams)[':teamId']['people']['$post']>[0]['json']
export function useCreatePerson(teamId: string, season: string) {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: (json: CreateBody) => unwrap(api.teams[':teamId'].people.$post({ param: { teamId }, query: { season }, json })),
    onSuccess: done,
  })
}

type MasterBody = Parameters<(typeof api.people)[':id']['$patch']>[0]['json']
export function useUpdatePerson(id: string) {
  const done = useInvalidatePeople()
  return useMutation({ mutationFn: (json: MasterBody) => unwrap(api.people[':id'].$patch({ param: { id }, json })), onSuccess: done })
}

type ContactBody = Parameters<(typeof api.people)[':id']['contact']['$put']>[0]['json']
export function useUpdateContact(id: string) {
  const done = useInvalidatePeople()
  return useMutation({ mutationFn: (json: ContactBody) => unwrap(api.people[':id'].contact.$put({ param: { id }, json })), onSuccess: done })
}

type SensitiveBody = Parameters<(typeof api.people)[':id']['sensitive']['$put']>[0]['json']
export function useUpdateSensitive(id: string) {
  const done = useInvalidatePeople()
  return useMutation({ mutationFn: (json: SensitiveBody) => unwrap(api.people[':id'].sensitive.$put({ param: { id }, json })), onSuccess: done })
}

type PhysicalBody = Parameters<(typeof api.people)[':id']['physicals']['$post']>[0]['json']
export function useAddPhysical(id: string) {
  const done = useInvalidatePeople()
  return useMutation({ mutationFn: (json: PhysicalBody) => unwrap(api.people[':id'].physicals.$post({ param: { id }, json })), onSuccess: done })
}

type MembershipBody = Parameters<(typeof api)['squad-memberships'][':id']['$patch']>[0]['json']
export function useUpdateMembership(season: string) {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: ({ id, ...json }: MembershipBody & { id: string }) =>
      unwrap(api['squad-memberships'][':id'].$patch({ param: { id }, query: { season }, json })),
    onSuccess: done,
  })
}

type AssessmentBody = Parameters<(typeof api.people)[':id']['assessments']['$post']>[0]['json']
export function useAddAssessment(id: string) {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: (json: AssessmentBody) => unwrap(api.people[':id'].assessments.$post({ param: { id }, json })),
    onSuccess: done,
  })
}

export function useLinkAction() {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'confirm' | 'unlink' }) =>
      action === 'confirm'
        ? unwrap(api['external-links'][':id'].confirm.$post({ param: { id } }))
        : unwrap(api['external-links'][':id'].$delete({ param: { id } })),
    onSuccess: done,
  })
}

export function useProfileLink(id: string) {
  return useMutation({ mutationFn: () => unwrap(api.people[':id']['profile-link'].$post({ param: { id } })) })
}

export function useUploadPhoto(id: string) {
  const done = useInvalidatePeople()
  return useMutation({
    mutationFn: async (file: Blob) => {
      const form = new FormData()
      form.append('file', file, 'foto.webp')
      await rawFetch(`/api/people/${id}/photo`, { method: 'POST', body: form })
    },
    onSuccess: done,
  })
}

export function useRemovePhoto(id: string) {
  const done = useInvalidatePeople()
  return useMutation({ mutationFn: () => unwrap(api.people[':id'].photo.$delete({ param: { id } })), onSuccess: done })
}

export type ImportPreview = {
  columns: { header: string; field: string | null; label: string | null }[]
  counts: { create: number; update: number; unchanged: number; skip: number }
  rows: {
    line: number
    action: 'create' | 'update' | 'unchanged' | 'skip'
    label: string
    issues: string[]
    reason: string | null
    changes: { field: string; label: string; from: unknown; to: unknown }[]
  }[]
}
export type ImportResult = { created: number; updated: number; unchanged: number; skipped: number; errors: { line: number; label: string; message: string }[] }

async function sendSheet<T>(path: string, file: File): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  const res = await rawFetch(path, { method: 'POST', body: form })
  return (await res.json()) as T
}

export const previewImport = (teamId: string, file: File) =>
  sendSheet<ImportPreview>(`/api/teams/${teamId}/squad/import/preview`, file)
export const applyImport = (teamId: string, season: string, file: File) =>
  sendSheet<ImportResult>(`/api/teams/${teamId}/squad/import/apply?season=${season}`, file)

/** URL for the photo of a person (served by the media module with permission checks). */
export const photoUrl = (assetId: string | null | undefined) => (assetId ? `/api/assets/${assetId}` : null)

export { scopeOf, loaderScope } from '~/lib/session'
